import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

export default class extends Controller {
  static targets = ["emailInput", "statusIndicator"];
  static values = { email: String, verifyEmailUrl: String };

  connect() {
    this.debounceTimer = null;
    this.pollingTimer = null;

    // Manually add event listeners to ensure they work
    this.emailInputTarget.addEventListener("blur", this.emailInputTargetBlur.bind(this));
    this.emailInputTarget.addEventListener("input", this.emailInputTargetInput.bind(this));

    // Check initial email status if there's a value
    if (this.emailValue && this.emailValue.trim() !== "") {
      this.checkEmailStatus(this.emailValue.trim());
    }
  }

  disconnect() {
    // Clean up event listeners
    this.emailInputTarget.removeEventListener("blur", this.emailInputTargetBlur.bind(this));
    this.emailInputTarget.removeEventListener("input", this.emailInputTargetInput.bind(this));
    this.clearDebounceTimer();
  }

  // Handle blur event (when user clicks away from input)
  emailInputTargetBlur() {
    const email = this.emailInputTarget.value.trim();
    if (email && this.isValidEmail(email)) {
      this.checkEmailStatus(email);
    }
  }

  // Handle input changes with debounce
  emailInputTargetInput() {
    this.clearDebounceTimer();

    const email = this.emailInputTarget.value.trim();
    if (!email) {
      this.clearStatus();
      return;
    }

    // Debounce the status check
    this.debounceTimer = setTimeout(() => {
      if (email && this.isValidEmail(email)) {
        this.checkEmailStatus(email);
      }
    }, 1000);
  }

  async checkEmailStatus(email) {
    if (!email || !this.isValidEmail(email)) {
      this.clearStatus();
      return;
    }

    this.showLoadingState();

    try {
      const response = await get(`${this.verifyEmailUrlValue}?email=${encodeURIComponent(email)}`, {
        responseKind: "json",
      });

      if (response.ok) {
        const data = await response.response.json();
        this.displayStatus(data);
      } else {
        this.displayStatus({ status: "ERROR", result: "ERROR" });
      }
    } catch (error) {
      console.error("Error checking email status:", error);
      this.displayStatus({ status: "ERROR", result: "ERROR" });
    }
  }

  displayStatus(statusData) {
    const indicator = this.statusIndicatorTarget;

    if (!statusData.status) {
      this.clearStatus();
      return;
    }

    const { icon, colorClass, title } = this.getStatusDisplay(statusData.result);

    indicator.innerHTML = `
      <span class="status-icon ${colorClass}" title="${title}">
        <i class="fa-solid ${icon}"></i>
        ${title}
      </span>
    `;
  }

  getStatusDisplay(result) {
    switch (result) {
      case "DELIVERABLE":
        return {
          icon: "fa-check-circle",
          colorClass: "text-success",
          title: `Deliverable`,
        };
      case "ERROR":
        return {
          icon: "fa-exclamation-circle",
          colorClass: "text-danger",
          title: `Error`,
        };
      case "RISKY":
        return {
          icon: "fa-exclamation-triangle",
          colorClass: "text-warning",
          title: `Risky`,
        };
      case "UNDELIVERABLE":
        return {
          icon: "fa-times-circle",
          colorClass: "text-danger",
          title: `Undeliverable`,
        };
      case "UNKNOWN":
      default:
        return {
          icon: "fa-question-circle",
          colorClass: "text-muted",
          title: `Unknown`,
        };
    }
  }

  showLoadingState() {
    const indicator = this.statusIndicatorTarget;
    indicator.innerHTML = `
      <span class="status-icon text-primary" title="Checking email...">
        <i class="fa-solid fa-spinner fa-spin"></i>
      </span>
    `;
  }

  clearStatus() {
    const indicator = this.statusIndicatorTarget;
    indicator.innerHTML = '<span class="status-placeholder"></span>';
  }

  clearDebounceTimer() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}
